/**
 * API Client for making HTTP requests
 *
 * PRODUCTION NOTES:
 * - Implement token refresh logic for expired tokens
 * - Add request/response interceptors for global error handling
 * - Use HttpOnly cookies instead of localStorage for token storage
 * - Implement proper CORS handling on backend
 */

import { checkInvalidToken } from '../utils/authGuard';
import { tokenManager } from '../utils/tokenManager';

export const BASE_URL = process.env.REACT_APP_API_URL;
export const AUTH_HOST = process.env.REACT_APP_AUTH_HOST;
export const USER_API_URL = process.env.REACT_APP_USER_API_URL;
export const COMMON_API_URL = process.env.REACT_APP_COMMON_API_URL;
export const SERVICES_API_URL = process.env.REACT_APP_COMMON_API_URL;
export const BOOKING_API_URL = process.env.REACT_APP_BOOKING_API_URL;
export const PAYMENT_API_URL = process.env.REACT_APP_PAYMENT_API_URL;
export const IMAGE_URL = process.env.REACT_APP_IMAGE_URL;

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number>;
}

/**
 * Get auth token from localStorage
 * PRODUCTION: Use HttpOnly cookies or secure token storage
 */
export function getAuthToken(): string | null {
  return localStorage.getItem('authToken');
}

/**
 * Set auth token
 * PRODUCTION: Backend should set HttpOnly cookie
 */
export function setAuthToken(token: string): void {
  localStorage.setItem('authToken', token);
}

/**
 * Remove auth token
 */
export function removeAuthToken(): void {
  localStorage.removeItem('authToken');
}

/**
 * Build URL with query parameters
 */
function buildUrl(endpoint: string, params?: Record<string, string | number>): string {
  const url = new URL(endpoint, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.append(key, String(value));
      }
    });
  }
  return url.toString();
}

/**
 * Generic API request handler
 */
async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { params, ...fetchOptions } = options;
  const url = buildUrl(endpoint, params);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add auth token if available
  let token = getAuthToken();
  if (token) {
    // Check if token is expiring soon and refresh it proactively
    if (tokenManager.isTokenExpiringSoon()) {
      try {
        token = await tokenManager.refreshToken();
      } catch (error) {
        console.warn('Proactive token refresh failed, will try on 401:', error);
      }
    }
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    // Check for invalid token
    if (checkInvalidToken(response)) {
      return undefined as T;
    }

    // Handle 401 errors - token expired
    if (response.status === 401) {
      try {
        // Try to refresh token and retry request
        const newToken = await tokenManager.refreshToken();
        headers['Authorization'] = `Bearer ${newToken}`;

        const retryResponse = await fetch(url, {
          ...fetchOptions,
          headers,
        });

        if (retryResponse.ok) {
          return await retryResponse.json();
        } else {
          throw new Error('Request failed after token refresh');
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // Token refresh failed, handle it
        tokenManager.handleRefreshFailure();
        return undefined as T;
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API Request failed:', error);
    throw error;
  }
}

export const apiClient = {
  get: <T>(endpoint: string, params?: Record<string, string | number>) =>
    request<T>(endpoint, { method: 'GET', params }),

  post: <T>(endpoint: string, data?: unknown) =>
    request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  patch: <T>(endpoint: string, data?: unknown) =>
    request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: <T>(endpoint: string) => request<T>(endpoint, { method: 'DELETE' }),
};

// Services API client using the SERVICES_API_URL
export const servicesClient = {
  get: <T>(endpoint: string, params?: Record<string, string | number>) =>
    request<T>(`${SERVICES_API_URL}${endpoint}`, { method: 'GET', params }),

  post: <T>(endpoint: string, data?: unknown) =>
    request<T>(`${SERVICES_API_URL}${endpoint}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  patch: <T>(endpoint: string, data?: unknown) =>
    request<T>(`${SERVICES_API_URL}${endpoint}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: <T>(endpoint: string) =>
    request<T>(`${SERVICES_API_URL}${endpoint}`, { method: 'DELETE' }),
};
